import { motion } from "framer-motion";
import { CheckCircle2, Sparkles, TrendingUp, Users, Zap, BarChart3 } from "lucide-react";
import { useState, useEffect } from "react";

const AIComparisonMockup = () => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setStep(1), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (step === 1) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => setStep(2), 300);
            return 100;
          }
          return prev + 5;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [step]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative w-full max-w-lg mx-auto"
    >
      {/* Main container */}
      <div className="relative glass rounded-2xl overflow-hidden border border-border/50">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-secondary/30">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-muted-foreground/40" />
            <div className="w-3 h-3 rounded-full bg-primary/60" />
            <div className="w-3 h-3 rounded-full bg-primary/40" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent" />
              <span>AI Campaign Builder</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Step 1: Processing */}
          {step >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Generating AI Campaign...</span>
                <span className="text-xs text-accent">{progress}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-accent"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2 pt-2">
                <div className="bg-secondary/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${progress >= 25 ? 'text-accent' : 'text-muted-foreground/40'}`} />
                  <span className="text-xs">Ad Copy Created</span>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${progress >= 50 ? 'text-accent' : 'text-muted-foreground/40'}`} />
                  <span className="text-xs">Targeting Set</span>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${progress >= 75 ? 'text-accent' : 'text-muted-foreground/40'}`} />
                  <span className="text-xs">Platforms Selected</span>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${progress >= 100 ? 'text-accent' : 'text-muted-foreground/40'}`} />
                  <span className="text-xs">A/B Tests Ready</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Results Dashboard */}
          {step >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-3 pt-2"
            >
              <div className="bg-card border border-accent/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-accent" />
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">Campaign Live</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Users className="h-4 w-4 text-accent" />
                      <span className="text-lg font-bold">847</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Impressions</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <TrendingUp className="h-4 w-4 text-accent" />
                      <span className="text-lg font-bold">12.4%</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">CTR</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <BarChart3 className="h-4 w-4 text-accent" />
                      <span className="text-lg font-bold">23</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Leads</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-2 text-xs text-accent">
                <div className="w-2 h-2 rounded-full bg-accent animate-ping" />
                <span>Running on ChatGPT, Gemini & Perplexity</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AIComparisonMockup;
